import {
  Session,
  SESSION_STATUSES,
  SCENARIO_TYPES,
  RISK_APPROACHES,
  HISTORICAL_SCENARIO,
  PLAYING,
  PAUSED,
  FINISHED,
  CREATING,
  DELAY,
  FROZEN,
  WEALTH_MANAGEMENT,
} from 'state';
import { formatters } from 'services';

/* =============================================================================
   Pure display helpers for the command-center Dashboard (v2).

   IMPORTANT — date-leak safety: on HISTORICAL sessions `scenario_date`,
   `start_date` and `end_date` are real market dates (a period-ID cheat vector,
   see player_date_leak_map.md). None of the models below read them. Progress is
   derived only from the safe, non-leaking `elapsed_days` / `total_simulated_days`
   / `remaining_*` fields; the only timestamps shown are the real wall-clock
   `create_date` / `finish_date`. Nothing here surfaces a simulated calendar date.
   ============================================================================= */

export type StatusTone = 'run' | 'pause' | 'fin' | 'create';

export type StatusMeta = {
  label: string;
  tone: StatusTone;
  live: boolean;
};

export function statusMeta(status: Session['status']): StatusMeta {
  switch (status) {
    case PLAYING:
      return { label: 'Running', tone: 'run', live: true };
    case PAUSED:
      return { label: 'Paused', tone: 'pause', live: false };
    case FINISHED:
      return { label: 'Finished', tone: 'fin', live: false };
    case CREATING:
      return { label: 'Setting up', tone: 'create', live: false };
    case DELAY:
      return { label: 'Delayed', tone: 'pause', live: false };
    case FROZEN:
      return { label: 'Frozen', tone: 'fin', live: false };
    default:
      return {
        label: SESSION_STATUSES[status] || 'Session',
        tone: 'create',
        live: false,
      };
  }
}

export const isFinished = (s: Session): boolean => s.status === FINISHED;

/** CTA label — matches the classic table's Join / View Returns split, expanded. */
export function ctaLabel(status: Session['status']): string {
  switch (status) {
    case FINISHED:
      return 'View returns';
    case PAUSED:
      return 'Resume';
    case CREATING:
      return 'Continue';
    default:
      return 'Open';
  }
}

/** Relative "3d ago" from a real wall-clock ISO timestamp (create/finish date). */
export function relativeAgo(iso?: string | null): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const sec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  const min = Math.floor(sec / 60);
  const hr = Math.floor(min / 60);
  const day = Math.floor(hr / 24);
  const mo = Math.floor(day / 30);
  const yr = Math.floor(day / 365);
  if (yr >= 1) return `${yr}y ago`;
  if (mo >= 1) return `${mo}mo ago`;
  if (day >= 1) return `${day}d ago`;
  if (hr >= 1) return `${hr}h ago`;
  if (min >= 1) return `${min}m ago`;
  return 'just now';
}

export type SessionCardModel = {
  id: number;
  title: string;
  subtitle: string;
  typeLabel: string;
  status: StatusMeta;
  tags: string[];
  aum: string;
  /** Real-time sessions don't show a progress bar; only historical ones do. */
  showProgress: boolean;
  progressPct: number;
  progressPrimary: string;
  progressSecondary: string;
  footText: string;
  cta: string;
  to: string;
};

/** Build the full display model for a session card from the login payload only. */
export function toCardModel(s: Session): SessionCardModel {
  const meta = statusMeta(s.status);
  const finished = isFinished(s);

  const total = s.total_simulated_days || 0;
  const elapsed = s.elapsed_days || 0;
  const progressPct =
    total > 0
      ? Math.min(100, Math.max(0, Math.round((elapsed / total) * 100)))
      : finished
        ? 100
        : 0;

  let progressPrimary: string;
  if (finished) {
    progressPrimary = total > 0 ? `${total} of ${total} days` : 'Completed';
  } else if (total > 0) {
    progressPrimary = `Day ${elapsed} of ${total}`;
  } else {
    progressPrimary = `${elapsed} day${elapsed === 1 ? '' : 's'} elapsed`;
  }

  // Countdown — remaining_session_time is a safe "HHh:MMmin" string (no date).
  const rt = (s.remaining_session_time || '').trim();
  const hasCountdown = !!rt && rt !== '00h:00min' && rt !== '0h:0min';
  let progressSecondary: string;
  if (finished) {
    progressSecondary = 'Complete';
  } else if (hasCountdown) {
    progressSecondary = `${rt} left`;
  } else if ((s.remaining_simulated_days || 0) > 0) {
    progressSecondary = `${s.remaining_simulated_days}d left`;
  } else {
    progressSecondary = '';
  }

  const mgmtShort =
    s.management_type === WEALTH_MANAGEMENT ? 'Wealth' : 'Portfolio';
  const instrumentCount =
    s.instrument_count ?? s.underlying_assets?.length ?? 0;
  const tags = [
    RISK_APPROACHES[s.risk_approach],
    mgmtShort,
    `${instrumentCount} instrument${instrumentCount === 1 ? '' : 's'}`,
  ].filter(Boolean) as string[];

  const footText = finished
    ? s.finish_date
      ? `Ended ${relativeAgo(s.finish_date)}`
      : 'Finished'
    : `Created ${relativeAgo(s.create_date)}`;

  return {
    id: s.id,
    title: s.klass_name || `Session #${s.id}`,
    subtitle: s.trainer_name ? `${s.trainer_name} · #${s.id}` : `#${s.id}`,
    typeLabel: SCENARIO_TYPES[s.scenario_type] || '',
    status: meta,
    tags,
    aum: formatters.millionDollars(s.starting_cash_amount),
    showProgress: s.scenario_type === HISTORICAL_SCENARIO,
    progressPct,
    progressPrimary,
    progressSecondary,
    footText,
    cta: ctaLabel(s.status),
    to: `/session/${s.id}`,
  };
}

/* ---- filtering / search / grouping ------------------------------------- */

export type StatusFilter = 'all' | 'active' | 'finished';
export type TypeFilter = 'all' | 'historical' | 'realtime';

export function matchesQuery(s: Session, q: string): boolean {
  if (!q) return true;
  const hay = `${s.klass_name || ''} ${s.trainer_name || ''} #${s.id} ${
    SCENARIO_TYPES[s.scenario_type] || ''
  } ${SESSION_STATUSES[s.status] || ''}`.toLowerCase();
  return hay.includes(q);
}

export function matchesStatus(s: Session, f: StatusFilter): boolean {
  if (f === 'all') return true;
  if (f === 'finished') return isFinished(s);
  return !isFinished(s); // active
}

export function matchesType(s: Session, f: TypeFilter): boolean {
  if (f === 'all') return true;
  if (f === 'historical') return s.scenario_type === 1;
  return s.scenario_type === 0; // realtime
}

/** Active first (running above the rest), then finished; newest within each. */
function byNewest(a: Session, b: Session): number {
  const ad = a.finish_date || a.create_date || '';
  const bd = b.finish_date || b.create_date || '';
  return bd.localeCompare(ad);
}

export function splitGroups(sessions: Session[]): {
  active: Session[];
  finished: Session[];
} {
  const active = sessions
    .filter(s => !isFinished(s))
    .sort((a, b) => {
      const ar = a.status === PLAYING ? 0 : 1;
      const br = b.status === PLAYING ? 0 : 1;
      return ar - br || byNewest(a, b);
    });
  const finished = sessions.filter(isFinished).sort(byNewest);
  return { active, finished };
}
